import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, CheckCircle, ChevronRight, ChevronDown, Search, AlertTriangle, MessageCircle } from 'lucide-react';
import { apiClient } from '../api';
import { useChat } from '../contexts/ChatContext';

const THREAT_STYLES = {
  high:      { text: 'text-red-400',     bg: 'bg-red-400/10',     border: 'border-red-400/20' },
  medium:    { text: 'text-amber-400',   bg: 'bg-amber-400/10',   border: 'border-amber-400/20' },
  low:       { text: 'text-emerald-400', bg: 'bg-emerald-400/10', border: 'border-emerald-400/20' },
  undefined: { text: 'text-gray-400',   bg: 'bg-gray-400/10',    border: 'border-gray-400/20' },
};

const SEVERITY_BORDER = {
  high:      'border-l-red-500',
  medium:    'border-l-amber-500',
  low:       'border-l-emerald-500',
  undefined: 'border-l-gray-700',
};

const IOC_TYPE_LABELS = {
  ip:       'IP Address',
  domain:   'Domain',
  hash:     'File Hash',
  filename: 'Filename',
  url:      'URL',
};

const KILL_CHAIN_ORDER = [
  'Reconnaissance',
  'Weaponization',
  'Delivery',
  'Exploitation',
  'Installation',
  'Command and Control',
  'Actions on Objectives',
];

const KILL_CHAIN_SHORT = {
  'Reconnaissance':        'Recon',
  'Weaponization':         'Weapn',
  'Delivery':              'Delivery',
  'Exploitation':          'Exploit',
  'Installation':          'Install',
  'Command and Control':   'C2',
  'Actions on Objectives': 'Actions',
};

const KILL_CHAIN_STYLES = {
  'Command and Control':   { text: 'text-pink-400',   bg: 'bg-pink-400/10',   border: 'border-pink-400/20',   ring: 'ring-pink-400/40' },
  'Actions on Objectives': { text: 'text-red-500',    bg: 'bg-red-500/10',    border: 'border-red-500/20',    ring: 'ring-red-500/40' },
  'Exploitation':          { text: 'text-red-400',    bg: 'bg-red-400/10',    border: 'border-red-400/20',    ring: 'ring-red-400/40' },
  'Installation':          { text: 'text-amber-400',  bg: 'bg-amber-400/10',  border: 'border-amber-400/20',  ring: 'ring-amber-400/40' },
  'Delivery':              { text: 'text-orange-400', bg: 'bg-orange-400/10', border: 'border-orange-400/20', ring: 'ring-orange-400/40' },
  'Reconnaissance':        { text: 'text-blue-400',   bg: 'bg-blue-400/10',   border: 'border-blue-400/20',   ring: 'ring-blue-400/40' },
  'Weaponization':         { text: 'text-purple-400', bg: 'bg-purple-400/10', border: 'border-purple-400/20', ring: 'ring-purple-400/40' },
};

const SEVERITY_ORDER = { high: 0, medium: 1, low: 2, undefined: 3 };

const buildAlertDescription = (alert, serverName) => {
  const asset = serverName || 'An asset';
  const val   = alert.ioc_value || 'unknown';
  const stage = alert.kill_chain_stage;
  const ctx   = stage ? ` (${stage})` : '';
  switch (alert.ioc_type) {
    case 'domain':
      return `${asset} made a DNS lookup or network connection to domain ${val}, which is flagged in threat intelligence feeds as a malicious indicator${ctx}.`;
    case 'ip':
      return `${asset} established a network connection to IP address ${val}, identified in threat intelligence feeds as a malicious host${stage ? ` associated with ${stage}` : ''}.`;
    case 'hash':
      return `A file matching hash ${val} was detected on ${asset}. This hash matches a known malicious file tracked in threat intelligence feeds${ctx}.`;
    case 'url':
      return `${asset} made an HTTP request to ${val}, which is flagged in threat intelligence feeds as a malicious URL${ctx}.`;
    case 'filename':
      return `A file named "${val}" was found on ${asset}, matching a known malicious filename in threat intelligence feeds${ctx}.`;
    default:
      return `Indicator "${val}" (type: ${alert.ioc_type || 'unknown'}) was detected on ${asset} and matched a threat intelligence feeds entry${ctx}.`;
  }
};

const relativeTime = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const KillChainBar = ({ stage }) => {
  if (!stage) return null;
  const activeIdx = KILL_CHAIN_ORDER.indexOf(stage);
  return (
    <div className="flex items-center gap-1 flex-wrap mt-1.5">
      {KILL_CHAIN_ORDER.map((s, i) => {
        const isActive = i === activeIdx;
        const style = KILL_CHAIN_STYLES[s] || {};
        return (
          <React.Fragment key={s}>
            <span className={`text-xs px-1.5 py-0.5 rounded border font-semibold transition-all ${
              isActive
                ? `${style.bg} ${style.text} ${style.border} ring-1 ${style.ring}`
                : 'bg-dark-700/40 text-gray-600 border-dark-600/40'
            }`}>
              {isActive ? '★ ' : ''}{KILL_CHAIN_SHORT[s]}
            </span>
            {i < KILL_CHAIN_ORDER.length - 1 && (
              <span className="text-gray-700 text-xs select-none">—</span>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const Threats = () => {
  const { openChatWithAlert } = useChat();
  const [alerts, setAlerts]           = useState([]);
  const [servers, setServers]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [resolvingId, setResolvingId] = useState(null);

  // Filters
  const [resolvedFilter,  setResolvedFilter]  = useState('0');
  const [serverFilter,    setServerFilter]    = useState('all');
  const [severityFilter,  setSeverityFilter]  = useState('all');
  const [searchQuery,     setSearchQuery]     = useState('');
  const [sortBy,          setSortBy]          = useState('newest');

  // Accordion & bulk select
  const [expandedId,    setExpandedId]    = useState(null);
  const [selectedIds,   setSelectedIds]   = useState(new Set());
  const [bulkResolving, setBulkResolving] = useState(false);
  const [resolvingPrivateIps, setResolvingPrivateIps] = useState(false);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (resolvedFilter !== 'all') params.set('resolved', resolvedFilter);
      if (serverFilter  !== 'all') params.set('server_id', serverFilter);
      const res = await apiClient.get(`/api/alerts/ioc?${params.toString()}`);
      setAlerts(res.data.ioc_alerts || []);
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Failed to fetch IoC alerts:', err);
      setError(err.response?.data?.error || 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, [resolvedFilter, serverFilter]);

  const fetchServers = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/servers?state=REGISTERED');
      setServers(res.data.servers || []);
    } catch (err) {
      console.error('Failed to fetch servers:', err);
    }
  }, []);

  useEffect(() => { fetchServers(); }, [fetchServers]);
  useEffect(() => { fetchAlerts();  }, [fetchAlerts]);

  const handleResolve = async (id) => {
    setResolvingId(id);
    try {
      await apiClient.patch(`/api/alerts/ioc/${id}`);
      setAlerts(prev => prev.filter(a => a.id !== id));
      setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      if (expandedId === id) setExpandedId(null);
    } catch (err) {
      console.error('Failed to resolve alert:', err);
      setError('Failed to resolve alert');
    } finally {
      setResolvingId(null);
    }
  };

  const handleBulkResolve = async () => {
    setBulkResolving(true);
    try {
      await Promise.all([...selectedIds].map(id => apiClient.patch(`/api/alerts/ioc/${id}`)));
      setAlerts(prev => prev.filter(a => !selectedIds.has(a.id)));
      if (selectedIds.has(expandedId)) setExpandedId(null);
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Failed to bulk resolve:', err);
      setError('Failed to resolve some alerts');
    } finally {
      setBulkResolving(false);
    }
  };

  const handleResolvePrivateIps = async () => {
    setResolvingPrivateIps(true);
    try {
      const res = await apiClient.post('/api/alerts/ioc/resolve-private-ips');
      await fetchAlerts();
      setError(null);
      console.log(`Resolved ${res.data.resolved} private IP alerts`);
    } catch (err) {
      console.error('Failed to resolve private IP alerts:', err);
      setError('Failed to resolve private IP alerts');
    } finally {
      setResolvingPrivateIps(false);
    }
  };

  const getServerName = useCallback((serverId) => {
    const server = servers.find(s => s.id === serverId);
    return server ? server.hostname : serverId;
  }, [servers]);

  // Per-IoC-value: how many distinct assets have seen it
  const dedupMap = useMemo(() => {
    const map = {};
    alerts.forEach(a => {
      if (!map[a.ioc_value]) map[a.ioc_value] = new Set();
      map[a.ioc_value].add(a.server_id);
    });
    return map;
  }, [alerts]);

  const filteredAlerts = useMemo(() => {
    let result = [...alerts];

    if (severityFilter !== 'all')
      result = result.filter(a => (a.threat_level || 'undefined') === severityFilter);

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(a => a.ioc_value?.toLowerCase().includes(q));
    }

    if (sortBy === 'severity')
      result.sort((a, b) =>
        (SEVERITY_ORDER[a.threat_level] ?? 3) - (SEVERITY_ORDER[b.threat_level] ?? 3));
    else if (sortBy === 'asset')
      result.sort((a, b) => getServerName(a.server_id).localeCompare(getServerName(b.server_id)));

    return result;
  }, [alerts, severityFilter, searchQuery, sortBy, getServerName]);

  const highCount   = alerts.filter(a => a.threat_level === 'high').length;
  const mediumCount = alerts.filter(a => a.threat_level === 'medium').length;

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const allSelected = filteredAlerts.length > 0 && selectedIds.size === filteredAlerts.length;
  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(filteredAlerts.map(a => a.id)));
  };

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">IoC Threat Alerts</h1>
          <p className="text-sm text-gray-500 mt-1">
            Indicators of Compromise matched against threat intelligence feeds
          </p>
        </div>
        <button onClick={fetchAlerts}
          className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-400 bg-dark-800 border border-dark-700 rounded-lg hover:text-white hover:border-gray-600 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
          <div className="text-3xl font-bold text-red-400">{highCount}</div>
          <div className="text-sm text-red-400/70">High Threat Level</div>
        </div>
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
          <div className="text-3xl font-bold text-amber-400">{mediumCount}</div>
          <div className="text-sm text-amber-400/70">Medium Threat Level</div>
        </div>
        <div className="bg-dark-800 border border-dark-700 rounded-xl p-4">
          <div className="text-3xl font-bold text-white">{alerts.length}</div>
          <div className="text-sm text-gray-400">Total IoC Alerts</div>
        </div>
      </div>

      {/* Controls */}
      <div className="space-y-3">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
          <input
            type="text"
            placeholder="Search by IoC value (IP, domain, hash, URL…)"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm rounded-lg border border-gray-700 bg-gray-800 text-gray-300 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500/30"
          />
        </div>

        {/* Filter row */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Severity chips */}
          <div className="flex items-center gap-1.5">
            {['all', 'high', 'medium', 'low'].map(s => {
              const style = s === 'all' ? null : THREAT_STYLES[s];
              const active = severityFilter === s;
              return (
                <button key={s} onClick={() => setSeverityFilter(s)}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                    active
                      ? s === 'all'
                        ? 'bg-orange-500/20 text-orange-400 border-orange-500/40'
                        : `${style.bg} ${style.text} ${style.border}`
                      : 'bg-dark-800 text-gray-500 border-dark-700 hover:text-gray-300 hover:border-gray-600'
                  }`}>
                  {s.toUpperCase()}
                </button>
              );
            })}
          </div>

          <div className="w-px h-4 bg-dark-700" />

          {/* Resolved filter */}
          <select value={resolvedFilter} onChange={e => setResolvedFilter(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-700 bg-gray-800 text-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500/30">
            <option value="0">Unresolved</option>
            <option value="1">Resolved</option>
            <option value="all">All</option>
          </select>

          {/* Asset filter */}
          <select value={serverFilter} onChange={e => setServerFilter(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-700 bg-gray-800 text-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500/30">
            <option value="all">All Assets</option>
            {servers.map(s => <option key={s.id} value={s.id}>{s.hostname}</option>)}
          </select>

          {/* Sort */}
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-700 bg-gray-800 text-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500/30">
            <option value="newest">Newest First</option>
            <option value="severity">By Severity</option>
            <option value="asset">By Asset</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="p-4 text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg">
          <strong>Error:</strong> {error}
          <button onClick={fetchAlerts} className="ml-4 text-sm underline hover:text-red-300">Retry</button>
        </div>
      )}

      {/* Private IP false-positive banner */}
      {(() => {
        const privateIpCount = alerts.filter(a =>
          a.resolved === 0 &&
          a.ioc_type === 'ip' &&
          /^(127\.|10\.|192\.168\.|169\.254\.|172\.(1[6-9]|2\d|3[01])\.)/.test(a.ioc_value)
        ).length;
        if (privateIpCount === 0) return null;
        return (
          <div className="flex items-start justify-between gap-4 p-4 bg-amber-400/10 border border-amber-400/20 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-400">
                  {privateIpCount} false positive alert{privateIpCount !== 1 ? 's' : ''} detected
                </p>
                <p className="text-xs text-amber-400/70 mt-0.5">
                  Private or loopback IPs (e.g. 127.0.0.1, 10.x, 192.168.x) cannot be external threats. These are safe to dismiss.
                </p>
              </div>
            </div>
            <button
              onClick={handleResolvePrivateIps}
              disabled={resolvingPrivateIps}
              className="flex-shrink-0 flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-amber-400 bg-amber-400/10 border border-amber-400/30 rounded-lg hover:bg-amber-400/20 transition-colors disabled:opacity-50">
              {resolvingPrivateIps
                ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Resolving…</>
                : <><CheckCircle className="w-3.5 h-3.5" /> Resolve all ({privateIpCount})</>}
            </button>
          </div>
        );
      })()}

      {/* Alert list */}
      <div className="bg-dark-800 border border-dark-700/50 rounded-xl overflow-hidden">

        {/* List header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-dark-700/50">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleSelectAll}
              title="Select all visible"
              className="w-4 h-4 rounded border-gray-600 bg-dark-700 accent-orange-500 cursor-pointer flex-shrink-0"
            />
            <div>
              <p className="text-sm font-semibold text-gray-300">IoC Alerts</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {filteredAlerts.length} alert{filteredAlerts.length !== 1 ? 's' : ''}
                {(searchQuery || severityFilter !== 'all') && alerts.length !== filteredAlerts.length
                  ? ` (filtered from ${alerts.length})`
                  : ' — matched from agent heartbeat & snapshot observables'}
              </p>
            </div>
          </div>

          {selectedIds.size > 0 && (
            <button onClick={handleBulkResolve} disabled={bulkResolving}
              className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 rounded-lg hover:bg-emerald-400/20 transition-colors disabled:opacity-50">
              {bulkResolving
                ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Resolving…</>
                : <><CheckCircle className="w-3.5 h-3.5" /> Resolve selected ({selectedIds.size})</>}
            </button>
          )}
        </div>
        {/* Rows */}
        <div className="max-h-[600px] overflow-y-auto overflow-x-hidden divide-y divide-dark-700/30">
          {loading ? (
            <div className="text-center py-10 text-gray-500 text-sm">Loading IoC alerts…</div>
          ) : filteredAlerts.length === 0 ? (
            <div className="text-center py-10 text-gray-500 text-sm">
              {searchQuery || severityFilter !== 'all'
                ? 'No alerts match your filters.'
                : 'No IoC alerts yet. Alerts are generated when agent connections or process hashes match threat intelligence feeds.'}
            </div>
          ) : (
            filteredAlerts.map(alert => {
              const threat      = THREAT_STYLES[alert.threat_level] || THREAT_STYLES.undefined;
              const leftBorder  = SEVERITY_BORDER[alert.threat_level] || SEVERITY_BORDER.undefined;
              const isExpanded  = expandedId === alert.id;
              const isSelected  = selectedIds.has(alert.id);
              const assetCount  = dedupMap[alert.ioc_value]?.size || 1;
              const serverName  = getServerName(alert.server_id);

              return (
                <div key={alert.id} className={`border-l-2 ${leftBorder} ${isSelected ? 'bg-dark-700/20' : ''}`}>

                  {/* ── Collapsed row ── */}
                  <div
                    className="flex items-center gap-2 sm:gap-3 px-4 py-3 hover:bg-dark-700/30 transition-colors cursor-pointer select-none"
                    onClick={() => setExpandedId(isExpanded ? null : alert.id)}
                  >
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={e => toggleSelect(alert.id, e)}
                      onClick={e => e.stopPropagation()}
                      className="w-4 h-4 rounded border-gray-600 bg-dark-700 accent-orange-500 cursor-pointer flex-shrink-0"
                    />

                    {/* Chevron */}
                    <span className="text-gray-500 flex-shrink-0">
                      {isExpanded
                        ? <ChevronDown className="w-3.5 h-3.5" />
                        : <ChevronRight className="w-3.5 h-3.5" />}
                    </span>

                    {/* ID */}
                    <span className="text-xs font-mono text-gray-500 w-12 flex-shrink-0">
                      #{String(alert.id).padStart(3, '0')}
                    </span>

                    {/* Severity + type + kill chain */}
                    <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap">
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${threat.bg} ${threat.text} border ${threat.border}`}>
                        {(alert.threat_level || 'UNDEF').toUpperCase()}
                      </span>
                      <span className="text-xs font-mono bg-dark-700/50 px-1.5 py-0.5 rounded text-gray-400 border border-dark-600/50">
                        {IOC_TYPE_LABELS[alert.ioc_type] || alert.ioc_type}
                      </span>
                      {alert.kill_chain_stage && (() => {
                        const ks = KILL_CHAIN_STYLES[alert.kill_chain_stage] || {};
                        return (
                          <span className={`text-xs px-1.5 py-0.5 rounded font-semibold border ${ks.bg || 'bg-gray-400/10'} ${ks.text || 'text-gray-400'} ${ks.border || 'border-gray-400/20'}`}>
                            {alert.kill_chain_stage}
                          </span>
                        );
                      })()}
                    </div>

                    {/* IoC value */}
                    <span className="text-sm font-mono text-gray-200 truncate flex-1 min-w-0">
                      {alert.ioc_value}
                    </span>

                    {/* Multi-asset badge */}
                    {assetCount > 1 && (
                      <span className="text-xs text-amber-400 bg-amber-400/10 border border-amber-400/20 px-1.5 py-0.5 rounded flex-shrink-0 whitespace-nowrap">
                        ×{assetCount} assets
                      </span>
                    )}

                    {/* Asset name */}
                    <span className="text-xs text-gray-500 flex-shrink-0 hidden md:block w-32 truncate" title={serverName}>
                      {serverName}
                    </span>

                    {/* Relative timestamp */}
                    <span
                      className="text-xs text-gray-500 flex-shrink-0 w-16 text-right"
                      title={alert.detected_at ? new Date(alert.detected_at).toLocaleString() : ''}>
                      {relativeTime(alert.detected_at)}
                    </span>

                    {/* Resolve button */}
                    <div className="flex-shrink-0 ml-1" onClick={e => e.stopPropagation()}>
                      {alert.resolved ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-400/10 text-emerald-400 border border-emerald-400/20">
                          Resolved
                        </span>
                      ) : (
                        <button
                          onClick={() => handleResolve(alert.id)}
                          disabled={resolvingId === alert.id}
                          className="text-gray-500 hover:text-emerald-400 transition-colors disabled:opacity-50"
                          title="Mark as resolved">
                          {resolvingId === alert.id
                            ? <RefreshCw className="w-4 h-4 animate-spin" />
                            : <CheckCircle className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* ── Expanded panel ── */}
                  {isExpanded && (
                    <div className="px-5 pb-5 pt-2 border-t border-dark-700/40 bg-dark-900/40">

                      {/* What happened */}
                      <div className="mt-3 mb-4 px-4 py-3 rounded-lg bg-dark-700/40 border border-dark-600/40">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">What Happened</p>
                        <p className="text-xs text-gray-300 leading-relaxed">
                          {buildAlertDescription(alert, serverName)}
                        </p>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">

                        {/* Indicator */}
                        <div className="space-y-1.5">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Indicator</p>
                          <p className="text-sm font-mono text-gray-100 break-all">{alert.ioc_value}</p>
                          <p className="text-xs text-gray-500">
                            Type: <span className="text-gray-400">{IOC_TYPE_LABELS[alert.ioc_type] || alert.ioc_type}</span>
                          </p>
                        </div>

                        {/* Threat intelligence */}
                        <div className="space-y-1.5">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Threat Intelligence</p>
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${threat.bg} ${threat.text} border ${threat.border}`}>
                            {(alert.threat_level || 'undefined').toUpperCase()}
                          </span>
                          {alert.source_event_id && (
                            <p className="text-xs text-gray-500">
                              Source Ref: <span className="font-mono text-gray-300">{alert.source_event_id}</span>
                            </p>
                          )}
                          {alert.description ? (
                            <p className="text-xs text-gray-400 leading-relaxed">{alert.description}</p>
                          ) : (
                            <p className="text-xs text-gray-600">No description available</p>
                          )}
                        </div>

                        {/* Attack context */}
                        <div className="space-y-1.5">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Attack Context</p>
                          {alert.attack_technique ? (
                            <p className="text-xs text-gray-500">
                              Technique: <span className="font-mono text-amber-400">{alert.attack_technique}</span>
                            </p>
                          ) : null}
                          {alert.kill_chain_stage ? (
                            <>
                              <p className="text-xs text-gray-500">Kill Chain Stage:</p>
                              <KillChainBar stage={alert.kill_chain_stage} />
                            </>
                          ) : null}
                          {!alert.attack_technique && !alert.kill_chain_stage && (
                            <p className="text-xs text-gray-600">No attack context available</p>
                          )}
                        </div>

                        {/* Asset & timeline */}
                        <div className="space-y-1.5">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Affected Asset & Timeline</p>
                          <p className="text-xs text-gray-500">
                            Asset: <span className="font-mono text-gray-300">{serverName}</span>
                          </p>
                          {assetCount > 1 && (
                            <p className="text-xs text-amber-400">
                              This indicator has been seen across <span className="font-semibold">{assetCount} assets</span>
                            </p>
                          )}
                          <p className="text-xs text-gray-500">
                            Detected:{' '}
                            <span className="text-gray-300">
                              {alert.detected_at ? new Date(alert.detected_at).toLocaleString() : '—'}
                            </span>
                          </p>
                          {alert.resolved_at && (
                            <p className="text-xs text-gray-500">
                              Resolved:{' '}
                              <span className="text-emerald-400">{new Date(alert.resolved_at).toLocaleString()}</span>
                            </p>
                          )}
                          {!alert.resolved && (
                            <button
                              onClick={() => handleResolve(alert.id)}
                              disabled={resolvingId === alert.id}
                              className="mt-1 flex items-center gap-1.5 px-3 py-1 text-xs font-semibold text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 rounded-lg hover:bg-emerald-400/20 transition-colors disabled:opacity-50">
                              {resolvingId === alert.id
                                ? <><RefreshCw className="w-3 h-3 animate-spin" /> Resolving…</>
                                : <><CheckCircle className="w-3 h-3" /> Mark as Resolved</>}
                            </button>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); openChatWithAlert([alert.id], 'ioc'); }}
                            className="mt-1 flex items-center gap-1.5 px-3 py-1 text-xs font-semibold text-primary bg-primary/10 border border-primary/20 rounded-lg hover:bg-primary/20 transition-colors"
                          >
                            <MessageCircle className="w-3 h-3" /> Discuss with AI
                          </button>
                        </div>

                      </div>

                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default Threats;
